/**
 * Tier-4 auras (M21, docs/TOWERS_V2.md "Tier 4"): the Beacon's two branches (what they lend the towers around them),
 * the Blizzard Shrine's slowing aura with Hailstorm and Frostbite, Radiance, Rally Cry, and the totems' Weakness and
 * Banish. Placement-time auras are folded into `recomputeAuras`; the live ones run every wave tick.
 */
import { DamageType } from "../../data/balance";
import { Fix64 } from "../../maths/fix64";
import { distanceSquared, positionAt } from "../../world/roadGeometry";
import { SimConstants } from "../constants";
import { Enemy, Tower } from "../entities";
import { SimEventKind } from "../events";
import type { LevelSim } from "../levelSim";
import { applyDamage } from "../systems/combat";
import { BASE_TIERS, branchOf, isBranch, skill } from "./ascension";
import { auraSlowOn, controllable } from "./statuses";

const PERCENT = 100;

/**
 * What a working-or-not Beacon `b` lends tower `t` inside its aura at tier 4: the damage bonus (the Lighthouse's own
 * replaces the Beacon's), War Banner haste, Fortune points, Guiding Light range. Null below tier 4 (the archetype's
 * bonuses apply as before).
 */
export interface AuraGift { readonly damage: Fix64; readonly points: Fix64; readonly haste: Fix64; readonly range: Fix64 }

export function auraGift(sim: LevelSim, b: Tower): AuraGift | null {
  const br = branchOf(sim, b);
  if (br === null) return null;
  const a = sim.balance.towers[b.archetype];
  const fortune = skill(sim, b, "fortune");
  const guide = skill(sim, b, "guidingLight");
  return {
    damage: br.tier4.num("auraDamageBonus", a.auraDamageBonus),
    points: a.auraPointsBonus.add(fortune?.params.num("pointsBonus") ?? Fix64.Zero),
    haste: br.tier4.num("haste"),
    range: guide?.params.num("rangeBonus") ?? Fix64.Zero,
  };
}

/** Distance² from tower `t`'s cell to (x, y). */
function d2From(t: Tower, x: Fix64, y: Fix64): Fix64 { return distanceSquared(Fix64.fromInt(t.cell.x), Fix64.fromInt(t.cell.y), x, y); }

/** Inside `t`'s aura and reachable: a Burrower underground is out of every aura's reach (ENEMIES_V2). */
function inRange(sim: LevelSim, t: Tower, e: Enemy): boolean { const r = sim.rangeOf(t); return !e.burrowed && d2From(t, e.x, e.y).le(r.mul(r)); }

/** Damage per tick of an aura's `dps` (a flat number at tower level 1), scaled like the tower's damage: level, gear, auras. */
function auraPerTick(sim: LevelSim, t: Tower, dps: Fix64): Fix64 {
  let d = dps.mul(sim.balance.tables.levelMultAt(sim.towerLevels[t.archetype]));
  const g = sim.gear[t.archetype].damage;
  if (!g.isZero()) d = d.mul(Fix64.One.add(g));
  if (!t.auraDamageMult.isZero() && !t.auraDamageMult.eq(Fix64.One)) d = d.mul(t.auraDamageMult);
  return d.divInt(SimConstants.TicksPerSecond);
}

/**
 * Once per wave tick (after the per-tick slows reset, before the towers fire): Blizzard Shrines slow everything in
 * range (Frostbite deepens it the longer an enemy stays, Hailstorm hurts), Lighthouses' Radiance burns, War Banners'
 * Rally Cry opens and lends its haste, Doom Totems' Weakness slows their cursed, Spirit Totems Banish.
 */
export function tickBranchAuras(sim: LevelSim): void {
  for (const t of sim.towerSlots) if (t.alive) t.rallyHaste = Fix64.Zero;
  for (const e of sim.enemySlots) if (e.alive) e.frostbiteTicks = frostbite(sim, e) ? e.frostbiteTicks + 1 : 0;
  for (const t of sim.towerSlots) {
    if (!t.alive || t.tier <= BASE_TIERS || t.disabledTicksLeft > 0) continue;
    const b = branchOf(sim, t);
    if (b === null) continue;
    switch (b.id) {
      case "blizzard": blizzard(sim, t, b.tier4.num("auraSlow")); break;
      case "lighthouse": radiance(sim, t); break;
      case "warbanner": rally(sim, t); break;
      case "doom": weakness(sim, t); break;
      case "spirit": banish(sim, t); break;
      default: break;
    }
  }
}

/** Whether the enemy stands inside a working Blizzard Shrine that knows Frostbite. */
function frostbite(sim: LevelSim, e: Enemy): boolean {
  for (const t of sim.towerSlots) {
    if (!t.alive || t.tier <= BASE_TIERS || t.disabledTicksLeft > 0 || skill(sim, t, "frostbite") === null) continue;
    if (inRange(sim, t, e)) return true;
  }
  return false;
}

function blizzard(sim: LevelSim, t: Tower, slow: Fix64): void {
  const bite = skill(sim, t, "frostbite");
  const hail = skill(sim, t, "hailstorm");
  const perTick = hail === null ? Fix64.Zero : auraPerTick(sim, t, hail.params.num("auraDps"));
  sim.areaDepth++;
  for (const e of sim.enemySlots) {
    if (!e.alive || !inRange(sim, t, e)) continue;
    let s = slow;
    if (bite !== null) s = Fix64.min(bite.params.num("maxSlow"), slow.add(bite.params.num("slowPerSecond").mulInt(e.frostbiteTicks).divInt(SimConstants.TicksPerSecond)));
    auraSlowOn(sim, e, s);
    if (perTick.isPositive()) applyDamage(sim, e, t, perTick, DamageType.Magic);
  }
  sim.areaDepth--;
}

function radiance(sim: LevelSim, t: Tower): void {
  const s = skill(sim, t, "radiance");
  if (s === null) return;
  const perTick = auraPerTick(sim, t, s.params.num("auraDps"));
  sim.areaDepth++;
  for (const e of sim.enemySlots) if (e.alive && inRange(sim, t, e)) applyDamage(sim, e, t, perTick, DamageType.Magic);
  sim.areaDepth--;
}

/** Rally Cry: every `cooldownTicks` a window of `activeTicks` in which every tower in the aura fires `haste` faster still. */
function rally(sim: LevelSim, t: Tower): void {
  const s = skill(sim, t, "rallyCry");
  if (s === null) return;
  if (t.skillTimer === 0 && t.skillActive === 0) {
    t.skillActive = s.params.int("activeTicks");
    t.skillTimer = s.params.int("cooldownTicks");
    sim.events.add(SimEventKind.SkillFired, t.id, s.index, 0);
  }
  if (t.skillActive <= 0) return;
  const r = sim.rangeOf(t), r2 = r.mul(r), haste = s.params.num("haste");
  for (const o of sim.towerSlots) {
    if (!o.alive || o === t) continue;
    if (d2From(t, Fix64.fromInt(o.cell.x), Fix64.fromInt(o.cell.y)).le(r2) && haste.gt(o.rallyHaste)) o.rallyHaste = haste;
  }
}

function weakness(sim: LevelSim, t: Tower): void {
  const s = skill(sim, t, "weakness");
  if (s === null) return;
  for (const e of sim.enemySlots) if (e.alive && e.hexTowerId === t.id) auraSlowOn(sim, e, s.params.num("slowFraction"));
}

/** Banish: every `cooldownTicks`, the cursed non-boss nearest the gate is sent `cells` back along the road (EnemyBanished). */
function banish(sim: LevelSim, t: Tower): void {
  const s = skill(sim, t, "banish");
  if (s === null || t.skillTimer > 0) return;
  let best: Enemy | null = null;
  for (const e of sim.enemySlots) {
    if (!e.alive || e.hexTowerId !== t.id || !controllable(e) || e.burrowed) continue;   // underground: its surfacing point would not move with it
    if (best === null || e.distance.gt(best.distance) || (e.distance.eq(best.distance) && e.id < best.id)) best = e;
  }
  if (best === null) return;
  const cells = s.params.num("cells");
  const back = Fix64.min(cells, best.distance);
  best.distance = best.distance.sub(back);
  [best.x, best.y] = positionAt(sim.road.roadCells, best.distance);
  best.cx = best.x.floorToInt(); best.cy = best.y.floorToInt();
  t.skillTimer = s.params.int("cooldownTicks");
  sim.events.add(SimEventKind.SkillFired, t.id, s.index, best.id);
  sim.events.add(SimEventKind.EnemyBanished, best.id, t.id, back.mulInt(PERCENT).floorToInt());
}

/** Whether `t` is a tier-4 Beacon branch (its aura gifts replace the archetype's numbers in recomputeAuras). */
export function isBeaconBranch(sim: LevelSim, t: Tower): boolean { return isBranch(sim, t, "lighthouse") !== null || isBranch(sim, t, "warbanner") !== null; }
